import {Component, OnInit, ViewChild} from '@angular/core';
import {IconDetailComponent} from "../../../../common/icon-detail/icon-detail.component";
import {ActivatedRoute, Router, RouterModule} from "@angular/router";
import {AccountService} from "../../../../domain/services/account/account.service";
import {AccountModel} from "../../../../domain/models/account.model";
import {ContactInfoCardComponent} from "../../../../common/contact-info-card/contact-info-card.component";
import {AddressInfoCardComponent} from "../../../../common/address-info-card/address-info-card.component";
import {MatDividerModule} from "@angular/material/divider";
import {MatTabsModule} from "@angular/material/tabs";
import {ProfileTabComponent} from "./components/profile-tab/profile-tab.component";
import {BillingTabComponent} from "./components/billing-tab/billing-tab.component";
import {PropertiesTabComponent} from "./components/properties-tab/properties-tab.component";
import {MatButton} from "@angular/material/button";
import {MatCard} from "@angular/material/card";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {RentalPropertyComponent} from "../../../property/rental-property/rental-property.component";
import {Icons} from '../../../../domain/enums/icons-enums';
import {CommonModule} from '@angular/common';
import {EnrollmentType, EnrollmentTypeStrMap} from "../../../../domain/enums/enrollment-type.enum";
import {AccountDetailMgmtCompanyComponent} from "./account-detail-mgmt-company/account-detail-mgmt-company.component";
import {
  AccountDetailIndividualBedComponent
} from "./account-detail-individual-bed/account-detail-individual-bed.component";
import {
  AccountDetailIndividualOwnerComponent
} from "./account-detail-individual-owner/account-detail-individual-owner.component";
import {DialogService} from "../../../../domain/services/dialog/dialog.service";
import {DIALOG} from "../../../../domain/constants/customer-search.constants";
import {CustomerFormComponent} from "./components/customer-form-component";
import {CustomerResumeBarComponent} from "../../../../common/customer-resume-bar/customer-resume-bar.component";
import {buildValidationErrorMessage} from "../../../../utils/invalid-fields.util";

@Component({
  selector: 'app-search-customer-detail',
  standalone: true,
  imports: [
    CustomerResumeBarComponent,
    IconDetailComponent,
    ContactInfoCardComponent,
    AddressInfoCardComponent,
    MatDividerModule,
    MatTabsModule,
    ProfileTabComponent,
    BillingTabComponent,
    PropertiesTabComponent,
    RouterModule,
    MatButton,
    MatCard,
    MatIcon,
    MatProgressSpinnerModule,
    RentalPropertyComponent,
    CommonModule,
    AccountDetailMgmtCompanyComponent,
    AccountDetailIndividualBedComponent,
    AccountDetailIndividualOwnerComponent,
  ],
  templateUrl: './search-customer-detail.component.html',
  styleUrl: './search-customer-detail.component.css'
})
export class SearchCustomerDetailComponent implements OnInit, CustomerFormComponent {
  @ViewChild(AccountDetailIndividualOwnerComponent) owner!: AccountDetailIndividualOwnerComponent;
  @ViewChild(AccountDetailIndividualBedComponent) bed!: AccountDetailIndividualBedComponent;
  @ViewChild(AccountDetailMgmtCompanyComponent) mgmtCompany!: AccountDetailMgmtCompanyComponent;
  currentAccount!: AccountModel;
  icons = Icons
  type: EnrollmentType | undefined;
  isLoading = false;
  // Index of the active detail tab; the profile Save/Go Back bar shows only on Profile (0).
  activeTabIndex = 0;
  protected readonly EnrollmentType = EnrollmentType;

  constructor(private readonly accountService: AccountService,
              private readonly dialogService: DialogService,
              private readonly route: ActivatedRoute,
              private readonly router: Router) {
  }

  ngOnInit(): void {
    // The open tab lives in the URL so leaving this page and coming back - viewing a receipt,
    // or the browser's own Back - returns to the tab the user was on rather than to Profile.
    this.route.queryParamMap.subscribe(q => {
      const tab = Number(q.get('tab'));
      this.activeTabIndex = Number.isFinite(tab) && tab >= 0 ? tab : 0;
    });
    const id: number = Number(this.route.snapshot.params['id']);
    if (id) {
      this.accountService.getById(id).subscribe(account => {
        this.currentAccount = account;
        if (this.currentAccount?.type) {
          this.type = EnrollmentTypeStrMap.get(this.currentAccount?.type);
        }
      }, error => {
        this.dialogService
          .openErrorDialog("Error",
          "An error occurred while trying to retrieve the account information.");
      })
    }
  }

  private getCustomerFormComponent(): CustomerFormComponent | undefined {
    switch (this.type){
      case EnrollmentType.PROPERTY_MANAGEMENT:
        return  this.mgmtCompany;
      case EnrollmentType.INDIVIDUAL_OWNER:
        return this.owner;
      case EnrollmentType.INDIVIDUAL_BED:
        return this.bed;
      default:
        return undefined;
    }
  }

  getUpdatedAccount(): AccountModel {
    const customerForm = this.getCustomerFormComponent();
    return customerForm? customerForm.getUpdatedAccount() : this.currentAccount;
  }

  isFormValid(): boolean {
    const customerForm = this.getCustomerFormComponent();
    return customerForm? customerForm.isFormValid() : false;
  }

  raiseFormValidations(): void {
    this.getCustomerFormComponent()?.raiseFormValidations();
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    // replaceUrl: one account visit should not leave a history entry per tab, or Back would
    // walk the tabs instead of leaving the account.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: index || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  save() {
    if(this.isFormValid()){
      this.sendUpdate();
    } else {
      this.raiseFormValidations();
      setTimeout(() => {
        const msg = buildValidationErrorMessage();
        this.dialogService.openErrorDialog("Unable to submit", msg);
      });
    }
  }

  private sendUpdate(){
    this.dialogService.openConfirmationDialog(DIALOG.title, DIALOG.message,() => {
      this.isLoading = true;
      this.accountService.update(this.getUpdatedAccount()).subscribe(() => {
        this.isLoading = false;
        this.dialogService
          .openSuccesDialog("Success",
            "Account information updated successfully.")
          .subscribe(() => {
            // Stay on the account, as saving from Billing already does. Being thrown back to
            // search after saving Profile loses the user's place and hides the saved result.
            this.reloadAccount();
          });
      }, error => {
        this.isLoading = false;
        this.dialogService
          .openErrorDialog("Error",
            "An error occurred while trying to update the account information.");
      });
    });
  }

  /** Re-reads the account so the form shows what was actually persisted. */
  private reloadAccount(): void {
    const id: number = Number(this.route.snapshot.params['id']);
    if (!id) { return; }
    this.accountService.getById(id).subscribe(account => {
      this.currentAccount = account;
      if (this.currentAccount?.type) {
        this.type = EnrollmentTypeStrMap.get(this.currentAccount?.type);
      }
    });
  }

  exit() {
    this.router.navigate(['app/search/customer']);
  }
}
